import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { CellPosition } from "@/shared/components/editable-table/useEditableRow";
import type { InspectionTemplateColumn } from "@/shared/models/Inspection";
import {
	Box,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import type { InspectionReading } from "../../models/Inspection";
import type { ReadingDraft } from "../../models/Reading";
import {
	ACTIONS_CLASS,
	DisplayReadingRow,
	EditReadingRow,
} from "./InspectionReadingRow";
import { textWidth } from "./textWidth";
import { SheetLastUpdate } from "../InspectionSheetPage";

const COL_WIDTH = {
	index: 30,
	time: 112,
	heat: 80,
	defects: 190,
	actions: 62,
} as const;

const COMPACT_COL_WIDTH = {
	...COL_WIDTH,
	time: 84,
	heat: 58,
	defects: 62,
} as const;

const FIXED_COLUMNS = Object.keys(COL_WIDTH).length;

const fixedWidthOf = (widths: Record<keyof typeof COL_WIDTH, number>) =>
	Object.values(widths).reduce((total, width) => total + width, 0);

const HEADER_TYPE = {
	fontSize: 10,
	fontWeight: 600,
} as const;

const headerWidth = (label: string) =>
	textWidth(label, {
		size: HEADER_TYPE.fontSize,
		weight: HEADER_TYPE.fontWeight,
	}) + 10;

const FLEX = { time: 1.5, heat: 1.5, defects: 3.5, value: 1 } as const;
const COMPACT_FLEX = { time: 0, heat: 0, defects: 0, value: 1 } as const;

const sum = (values: Iterable<number>) => {
	let total = 0;
	for (const value of values) total += value;
	return total;
};

const useAvailableWidth = () => {
	const ref = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;

		const observer = new ResizeObserver(([entry]) =>
			setWidth(entry.contentRect.width),
		);
		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	return { ref, width };
};

type SheetColumn = {
	id: number;
	label: string;
	group: string | null;
	minWidth: number;
	isGoNoGo: boolean;
};

const MIN_CHARS_WIDTH = textWidth("0".repeat(6), { size: 11, weight: 400 }) + 9;

const MIN_VALUE_WIDTH = {
	datetime: 104,
	length: 60,
	value: Math.max(48, MIN_CHARS_WIDTH),
} as const;

const minValueWidth = (column: InspectionTemplateColumn) => {
	if (column.ValueTypeCode === "D") return MIN_VALUE_WIDTH.datetime;
	if (column.ValueTypeCode === "L") return MIN_VALUE_WIDTH.length;

	return MIN_VALUE_WIDTH.value;
};

const withMinChars = (widths: Record<keyof typeof COL_WIDTH, number>) => ({
	...widths,
	time: Math.max(widths.time, MIN_CHARS_WIDTH),
	heat: Math.max(widths.heat, MIN_CHARS_WIDTH),
	defects: Math.max(widths.defects, MIN_CHARS_WIDTH),
});

const toSheetColumns = (columns: InspectionTemplateColumn[]): SheetColumn[] =>
	columns.map((column) => ({
		id: column.ColumnId as number,
		label:
			column.ValueTypeCode === "G"
				? `${column.ColumnLabel} *`
				: column.ColumnLabel,
		group: column.GroupName ?? null,
		minWidth: minValueWidth(column),
		isGoNoGo: column.ValueTypeCode === "G",
	}));

type HeaderGroup = {
	name: string | null;
	columns: SheetColumn[];
};

const buildHeaderGroups = (columns: SheetColumn[]) => {
	const groups: HeaderGroup[] = [];

	for (const column of columns) {
		const last = groups[groups.length - 1];

		if (column.group && last && last.name === column.group)
			last.columns.push(column);
		else groups.push({ name: column.group, columns: [column] });
	}

	return groups;
};

const valueColumnWidths = (groups: HeaderGroup[]) => {
	const widths = new Map<number, number>();

	for (const group of groups) {
		for (const column of group.columns)
			widths.set(
				column.id,
				Math.max(column.minWidth, headerWidth(column.label)),
			);

		if (!group.name) continue;

		const covered = sum(
			group.columns.map((column) => widths.get(column.id) ?? 0),
		);
		const missing = headerWidth(group.name) - covered;

		if (missing <= 0) continue;

		const share = Math.ceil(missing / group.columns.length);

		for (const column of group.columns)
			widths.set(column.id, (widths.get(column.id) ?? 0) + share);
	}

	return widths;
};

const buildSheetLayout = (
	columns: InspectionTemplateColumn[],
	showActions: boolean,
	compact: boolean,
) => {
	const colWidth = withMinChars(compact ? COMPACT_COL_WIDTH : COL_WIDTH);
	const flex = compact ? COMPACT_FLEX : FLEX;
	const sheetColumns = toSheetColumns(columns);
	const groups = buildHeaderGroups(sheetColumns);
	const widths = valueColumnWidths(groups);
	const named = groups.filter((group) => group.name !== null);
	const fixedWidth = showActions
		? fixedWidthOf(colWidth)
		: fixedWidthOf(colWidth) - colWidth.actions;
	const fixedColumns = showActions ? FIXED_COLUMNS : FIXED_COLUMNS - 1;

	return {
		colWidth,
		flex,
		sheetColumns,
		groups,
		widths,
		hasGroups: named.length > 0,
		groupedColumns: named.flatMap((group) => group.columns),
		sheetWidth: fixedWidth + sum(widths.values()),
		totalFlex:
			flex.time + flex.heat + flex.defects + sheetColumns.length * flex.value,
		columnCount: sheetColumns.length + fixedColumns,
	};
};

const MessageOverlay = ({
	top,
	children,
}: {
	top: number;
	children: ReactNode;
}) => (
	<Box
		sx={{
			position: "absolute",
			top,
			left: 0,
			right: 0,
			bottom: 0,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			px: 2,
			textAlign: "center",
			pointerEvents: "none",
		}}
	>
		{children}
	</Box>
);

type Props = {
	columns: InspectionTemplateColumn[];
	sectionId: number | null;
	readings: InspectionReading[];
	draft: ReadingDraft | null;
	editingRowId: number | null;
	isLoading: boolean;
	isMutating?: boolean;
	readOnly: boolean;
	showActions?: boolean;
	showSummary?: boolean;
	compact?: boolean;
	highlight?: SheetLastUpdate | null;
	onStartEdit?: (reading: InspectionReading) => void;
	onCancelEdit?: () => void;
	onDraftChange?: (draft: ReadingDraft) => void;
	onSaveDraft?: (draft: ReadingDraft) => void;
	onDelete?: (reading: InspectionReading) => void;
};

export const InspectionSheetTable = ({
	columns,
	sectionId,
	readings,
	draft,
	editingRowId,
	isLoading,
	isMutating = false,
	readOnly,
	showActions = true,
	showSummary = true,
	compact = false,
	highlight = null,
	onStartEdit = () => {},
	onCancelEdit = () => {},
	onDraftChange = () => {},
	onSaveDraft = () => {},
	onDelete = () => {},
}: Props) => {
	const { ref: containerRef, width: available } = useAvailableWidth();
	const [editCell, setEditCell] = useState<CellPosition | null>(null);

	const {
		colWidth,
		flex,
		sheetColumns,
		groups,
		widths,
		hasGroups,
		groupedColumns,
		sheetWidth,
		totalFlex,
		columnCount,
	} = buildSheetLayout(columns, showActions, compact);

	const defectCount = sum(readings.map((reading) => reading.Defects.length));

	const usable = Math.floor(available);
	const surplus = Math.max(0, usable - sheetWidth);
	const share = (min: number, flex: number) =>
		min + Math.floor((flex / totalFlex) * surplus);

	const timeWidth = share(colWidth.time, flex.time);
	const heatWidth = share(colWidth.heat, flex.heat);
	const valueWidths = new Map(
		sheetColumns.map((column) => [
			column.id,
			share(widths.get(column.id) ?? MIN_VALUE_WIDTH.value, flex.value),
		]),
	);

	const baseDefectsWidth = share(colWidth.defects, flex.defects);
	const used =
		colWidth.index +
		timeWidth +
		heatWidth +
		sum(valueWidths.values()) +
		baseDefectsWidth +
		(showActions ? colWidth.actions : 0);

	const defectsWidth = baseDefectsWidth + Math.max(0, usable - used);

	const headCell = {
		rowSpan: hasGroups ? 2 : 1,
		sx: { top: 0, borderBottom: "1px solid" },
	};

	const isEmpty = readings.length === 0 && !draft;

	const editRowProps = draft && {
		draft,
		columns,
		sectionId,
		defectsWidth,
		columnCount,
		isMutating,
		onChange: onDraftChange,
		onSave: () => onSaveDraft(draft),
		onCancel: onCancelEdit,
	};

	return (
		<Paper
			elevation={2}
			sx={{
				height: "100%",
				flexGrow: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				position: "relative",
			}}
		>
			{isMutating && (
				<LinearProgress
					sx={{
						left: 0,
						pointerEvents: "none",
						position: "absolute",
						right: 0,
						top: 0,
						zIndex: 3,
					}}
				/>
			)}

			<Box
				sx={{
					position: "relative",
					flex: 1,
					minHeight: 0,
					display: "flex",
				}}
			>
				<TableContainer ref={containerRef} sx={{ flex: 1, minWidth: 0 }}>
					<Table
						stickyHeader
						size="small"
						sx={{
							tableLayout: "fixed",
							minWidth: sheetWidth,
							"& td": {
								borderRight: "1px solid",
								borderColor: "divider",
								boxSizing: "border-box",
								fontSize: 11,
								height: 30,
								lineHeight: 1.2,
								px: 0.5,
								py: 0,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							},
							"& th": {
								...HEADER_TYPE,
								backgroundColor: "#fff",
								borderRight: "1px solid",
								borderColor: "divider",
								color: "text.secondary",
								lineHeight: `22px`,
								position: "sticky",
								px: 0.5,
								py: 0,
								textAlign: "center",
								verticalAlign: "middle",
								whiteSpace: "nowrap",
								zIndex: 2,
							},
							[`& .${ACTIONS_CLASS}`]: {
								position: "sticky",
								right: 0,
								zIndex: 1,
								boxShadow: "-4px 0 4px -4px rgba(0, 0, 0, 0.25)",
							},
							[`& th.${ACTIONS_CLASS}`]: { zIndex: 3 },
						}}
					>
						<colgroup>
							<col style={{ width: colWidth.index }} />
							<col style={{ width: timeWidth }} />
							<col style={{ width: heatWidth }} />
							{sheetColumns.map((column) => (
								<col
									key={column.id}
									style={{ width: valueWidths.get(column.id) }}
								/>
							))}
							<col style={{ width: defectsWidth }} />
							{showActions && <col style={{ width: colWidth.actions }} />}
						</colgroup>

						<TableHead>
							<TableRow>
								<TableCell {...headCell}>#</TableCell>
								<TableCell {...headCell}>Time</TableCell>
								<TableCell {...headCell}>Heat</TableCell>

								{groups.map((group, index) =>
									group.name ? (
										<TableCell
											key={`group-${index}-${group.name}`}
											data-test-id="sheet-group-header"
											colSpan={group.columns.length}
											sx={{ top: 0 }}
										>
											{group.name}
										</TableCell>
									) : (
										<TableCell
											key={`column-${group.columns[0].id}`}
											data-test-id="sheet-column-header"
											title={
												group.columns[0].isGoNoGo ? "Go / No Go" : undefined
											}
											{...headCell}
										>
											{group.columns[0].label}
										</TableCell>
									),
								)}

								<TableCell {...headCell} title="Surface defects">
									{compact ? "Defects" : "Surface defects"}
								</TableCell>
								{showActions && (
									<TableCell className={ACTIONS_CLASS} {...headCell} />
								)}
							</TableRow>

							{hasGroups && (
								<TableRow>
									{groupedColumns.map((column) => (
										<TableCell
											key={column.id}
											data-test-id="sheet-column-header"
											title={column.isGoNoGo ? "Go / No Go" : undefined}
											sx={{
												top: 22,
												borderBottom: "1px solid",
											}}
										>
											{column.label}
										</TableCell>
									))}
								</TableRow>
							)}
						</TableHead>

						<TableBody>
							{!isLoading && (
								<>
									{readings.map((reading, index) =>
										editingRowId === reading.RowId && editRowProps ? (
											<EditReadingRow
												key={`edit-${reading.RowId}`}
												index={index + 1}
												focusCell={editCell}
												{...editRowProps}
											/>
										) : (
											<DisplayReadingRow
												key={reading.RowId}
												index={index + 1}
												reading={reading}
												columns={columns}
												columnCount={columnCount}
												defectsWidth={defectsWidth}
												compactDefects={compact}
												isLastUpdated={highlight?.rowId === reading.RowId}
												disabled={draft !== null || readOnly}
												canDoubleClickEdit={!readOnly && !isMutating}
												showActions={showActions}
												onEdit={(cell) => {
													setEditCell(cell);
													onStartEdit(reading);
												}}
												onDelete={() => onDelete(reading)}
											/>
										),
									)}

									{draft?.RowId === null && editRowProps && (
										<EditReadingRow
											key="new-reading"
											index={readings.length + 1}
											{...editRowProps}
										/>
									)}
								</>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{(isLoading || isEmpty) && (
					<MessageOverlay top={hasGroups ? 46 : 23}>
						{isLoading ? (
							<CenteredLazyLoading />
						) : (
							<Typography
								variant="body2"
								color="text.secondary"
								data-test-id="sheet-empty-message"
							>
								{readOnly ? (
									"Nothing was read on this sheet."
								) : (
									<>
										No readings yet. Click <b>Add Reading</b> to start filling
										the sheet.
									</>
								)}
							</Typography>
						)}
					</MessageOverlay>
				)}
			</Box>

			{showSummary && (
				<Box
					sx={{
						borderTop: "1px solid",
						borderColor: "divider",
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						px: 1.5,
						py: 0.25,
					}}
				>
					<Summary
						label="Readings"
						value={readings.length}
						testId="readings-count"
					/>
					<Summary label="Defects" value={defectCount} testId="defects-count" />
				</Box>
			)}
		</Paper>
	);
};

const Summary = ({
	label,
	value,
	testId,
}: {
	label: string;
	value: number;
	testId: string;
}) => (
	<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
		<Typography sx={{ fontSize: 11 }} color="text.secondary">
			{`${label}:`}
		</Typography>
		<Typography
			sx={{
				fontSize: 12,
				fontWeight: 600,
				fontVariantNumeric: "tabular-nums",
			}}
			color="primary.main"
			data-test-id={testId}
		>
			{value.toLocaleString()}
		</Typography>
	</Box>
);
